import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { MostExecutedModelsQuery } from '__generated__/graphql';
import { max } from 'd3';

import {
  Accessor,
  BarGroups,
  Bars,
  ButtonGroup,
  Card,
  CategoryAxis,
  categoryScale,
  ChartProps,
  InteractionItem,
  LinearAxis,
  linearScale,
  Plot,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAdminEnvironment } from '@/hooks/admin/environments';
import { useMostExecutedModels } from '@/hooks/discovery/performance/mostExecutedModels';
import { useMostExecutedModelsReusedCounts } from '@/hooks/discovery/performance/useMostExecutedModelsReusedCounts';
import { useLinkToDbtCloud, useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { ArrElement } from '@/typings/arrayElement';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { ChartDateDropdown, DateRange, DEFAULT_DATE_RANGE } from './ChartDateDropdown';
import { ChartWrapper } from './ChartWrapper';

export interface MostExecutedModelsColumnChartProps {}

type MostExecutedModel = ArrElement<
  MostExecutedModelsQuery['performance']['mostExecutedModels']
>;

export const MostExecutedModelsColumnChart: FC<
  MostExecutedModelsColumnChartProps
> = () => {
  const [selectedTab, setSelectedTab] = useState<'Max' | 'Job'>('Max');

  const { accountId, projectId, environmentId } = useDbtCloudProject();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);

  const getResourceDetailsPageUrl = useLinkToExplorer('resourceDetails');
  const navigate = useNavigate();

  const projectScope = useUrlProjectParams();
  const getJobLink = useLinkToDbtCloud('jobDetails');

  const [mostExecutedModels] = useMostExecutedModels({
    environmentId,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limit: 5,
  });

  if (!accountId || !projectId) {
    throw new Error(
      'This component expects to be in a URL with accountId and projectId parameters!',
    );
  }

  const environment = useAdminEnvironment(
    environmentId,
    accountId,
    undefined,
    true,
    selectedTab === 'Job',
  );

  const jobNamesById = (environment?.data?.jobs || []).reduce<Record<number, string>>(
    (acc, job) => {
      acc[job.id] = job.name;
      return acc;
    },
    {},
  );

  const rawData = mostExecutedModels.data?.performance?.mostExecutedModels;
  const numJobs = rawData?.[0]?.byJob?.length ?? 0;

  const uniqueIds = useMemo(() => (rawData ?? []).map((m) => m.uniqueId), [rawData]);
  const {
    reusedByModel,
    reusedByModelAndJob,
    fetching: reusedFetching,
  } = useMostExecutedModelsReusedCounts(
    uniqueIds,
    environmentId,
    dateRange.startDate,
    dateRange.endDate,
  );

  const getNonReusedExecutions = useCallback(
    (model: MostExecutedModel) =>
      (model.totalExecutions || 0) - (reusedByModel[model.uniqueId] ?? 0),
    [reusedByModel],
  );

  const getNonReusedJobExecutions = useCallback(
    (model: MostExecutedModel, jobId: number) => {
      const jobTotal =
        model.byJob?.find((j) => j.jobId === jobId)?.totalExecutions || 0;
      const jobReused = reusedByModelAndJob[model.uniqueId]?.[jobId] ?? 0;
      return jobTotal - jobReused;
    },
    [reusedByModelAndJob],
  );

  const chartProps: ChartProps | null = useMemo(() => {
    const chartData = rawData ?? [];

    const xAccessor: Accessor<MostExecutedModel, string> = (x) =>
      parseUniqueId(x.uniqueId).remainder;
    const yScale = linearScale({
      min: 0,
      max: max(chartData, (x) => getNonReusedExecutions(x)) || 0,
    });
    const xScale = categoryScale({ labels: chartData.map(xAccessor) });

    const chartProps: Partial<ChartProps> = {
      margin: { left: 50, right: 5, top: 20, bottom: 40 },
      yScale,
      xScale,
      xAccessor,
      yAccessor: (x) => getNonReusedExecutions(x),
      tooltip: undefined,
      onItemClick(item: InteractionItem<MostExecutedModel>) {
        const nodeIsDeleted = item?.item.isDeleted;
        if (nodeIsDeleted) return;

        const url = getResourceDetailsPageUrl({
          ...projectScope,
          dbtUniqueId: item?.item?.uniqueId || '',
          tab: 'performance',
        });
        navigate(url);
      },
    };

    if (selectedTab === 'Max') {
      chartProps.yAccessor = (x) => getNonReusedExecutions(x);
      chartProps.tooltip = ({
        interactionItem,
      }: TooltipCallbackArgs<MostExecutedModel>) => {
        if (!interactionItem?.item) {
          return null;
        }
        const nodeIsDeleted = interactionItem.item.isDeleted;
        const nonReusedExecutions = getNonReusedExecutions(interactionItem.item);
        return (
          <div>
            <div className="text-xs">
              {nonReusedExecutions.toLocaleString()} executions (excludes reused)
            </div>
            {nodeIsDeleted && (
              <div className="text-xs">
                <i>This is a deleted model.</i>
              </div>
            )}
          </div>
        );
      };
    } else {
      chartProps.yAccessor = (x: MostExecutedModel) =>
        max(x.byJob || [], (j) => getNonReusedJobExecutions(x, j.jobId || 0)) || 0;
      chartProps.tooltip = ({
        interactionItem,
      }: TooltipCallbackArgs<MostExecutedModel>) => {
        if (!interactionItem?.item) {
          return null;
        }
        const nodeIsDeleted = interactionItem.item.isDeleted;
        return (
          <div className="flex flex-col space-y-2">
            {interactionItem.item?.byJob?.map((j, i) => {
              const projectLink = getJobLink?.({
                ...projectScope,
                jobId: j.jobId || 0,
                projectId: projectId || 0,
              });

              const nonReusedJobExecs = getNonReusedJobExecutions(
                interactionItem.item,
                j.jobId || 0,
              );

              return (
                <div key={i}>
                  <div className="text-sm">
                    {projectLink ? (
                      <Link to={projectLink} className="text-fgBrand">
                        Job: {jobNamesById[j.jobId || 0] || j.jobId}
                      </Link>
                    ) : (
                      <>Job: {jobNamesById[j.jobId || 0] || j.jobId}</>
                    )}
                  </div>
                  <div className="text-xs">
                    Executions: {nonReusedJobExecs.toLocaleString()}
                  </div>
                  {nodeIsDeleted && (
                    <div className="text-xs">
                      <i>This is a deleted model.</i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      };
    }

    return chartProps as ChartProps;
  }, [
    rawData,
    selectedTab,
    getResourceDetailsPageUrl,
    projectId,
    navigate,
    getJobLink,
    projectScope,
    jobNamesById,
    getNonReusedExecutions,
    getNonReusedJobExecutions,
  ]);

  useEffect(() => {
    if (numJobs === 1) {
      setSelectedTab('Job');
    }
  }, [numJobs]);

  const selectedChartTestId =
    selectedTab === 'Job'
      ? 'most-executed-models-chart-by-job'
      : 'most-executed-models-chart';
  return (
    <Card>
      <div data-testid="most-executed-models-chart-container">
        <div
          className="mb-5 flex flex-wrap justify-end space-y-2"
          data-testid="most-executed-models-chart-header"
        >
          <div
            className={`ml-[${chartProps?.margin.left}px] flex-1 justify-start whitespace-nowrap text-xl`}
            data-testid="most-executed-models-chart-title"
          >
            Most executed models (excludes reused)
          </div>
          {!!numJobs && numJobs > 1 && (
            <ButtonGroup testId="most-executed-models-chart-controls-grain">
              <ButtonGroup.Button
                text="Max"
                onClick={() => setSelectedTab('Max')}
                isDisabled={selectedTab === 'Max'}
                testId="most-executed-models-chart-controls-grain-max"
              />
              <ButtonGroup.Button
                text="Job"
                onClick={() => setSelectedTab('Job')}
                isDisabled={selectedTab === 'Job'}
                testId="most-executed-models-chart-controls-grain-job"
              />
            </ButtonGroup>
          )}
          <ChartDateDropdown
            onChange={setDateRange}
            testId="most-executed-models-chart-controls-date-dropdown"
          />
        </div>
        <ChartWrapper
          numDataPoints={rawData?.length}
          noDataMessage="No models were executed in this time period"
          {...chartProps}
          height={350}
          error={mostExecutedModels.error?.message}
          isLoading={mostExecutedModels.fetching || reusedFetching}
          testId={selectedChartTestId}
        >
          <CategoryAxis axis="x" label="Model: Max execution count" />
          <LinearAxis axis="y" label="Number of executions" />
          <Plot
            data={rawData ?? []}
            data-testid={`${selectedChartTestId}-plot`}
            itemComponent={selectedTab === 'Max' ? Bars : BarGroups}
            extraItemProps={{
              className:
                'fill-fgVizIndigo transition-all hover:fill-fgVizIndigoHover hover:cursor-pointer',
              ...(selectedTab === 'Job'
                ? {
                    groupValuesAccessor: (item: MostExecutedModel) =>
                      (item.byJob || []).map((x) =>
                        getNonReusedJobExecutions(item, x.jobId || 0),
                      ),
                  }
                : {}),
            }}
          />
        </ChartWrapper>
      </div>
    </Card>
  );
};
